import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { post, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, MessageSquareText, Loader2, Phone, Mail, FileDown, Clock } from "lucide-react";
import { fmtDate, fmtDateTime, nok } from "@/lib/format";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { initialsOf, publicPdfUrl } from "./proposalUtils";

/** Animated check: circle + tick drawn with pathLength. */
const CheckDraw = () => (
  <svg viewBox="0 0 48 48" className="h-10 w-10 shrink-0" aria-hidden="true">
    <motion.circle cx="24" cy="24" r="21" fill="none" stroke="#34d399" strokeWidth="3" initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: "easeOut" }} />
    <motion.path d="M15 24.5l6.5 6.5L33.5 18" fill="none" stroke="#34d399" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.45, ease: "easeOut" }} />
  </svg>
);

const STORAGE = "si_proposal_identity";
const loadIdentity = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE) || "null") || null; } catch { return null; }
};
const saveIdentity = (v) => {
  try { localStorage.setItem(STORAGE, JSON.stringify({ name: v.name, title: v.title, email: v.email })); } catch { /* ignore */ }
};

const useIdentity = (data) => {
  const c = data.customer?.contact || {};
  const [f, setF] = useState(() => loadIdentity() || { name: c.name || "", title: c.title || "", email: c.email || "" });
  return [f, setF];
};

const IdentityFields = ({ f, setF, prefix }) => (
  <div className="grid gap-4 sm:grid-cols-2">
    <div className="space-y-1.5 sm:col-span-2">
      <Label htmlFor={`${prefix}-name`}>Navn <span className="text-destructive">*</span></Label>
      <Input id={`${prefix}-name`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Fullt navn" autoComplete="name" className="h-10" data-testid={`${prefix}-name-input`} />
    </div>
    <div className="space-y-1.5">
      <Label htmlFor={`${prefix}-title`}>Stilling / rolle</Label>
      <Input id={`${prefix}-title`} value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="F.eks. Prosjektleder" autoComplete="organization-title" className="h-10" data-testid={`${prefix}-title-input`} />
    </div>
    <div className="space-y-1.5">
      <Label htmlFor={`${prefix}-email`}>E-post</Label>
      <Input id={`${prefix}-email`} type="email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="navn@firma.no" autoComplete="email" className="h-10" data-testid={`${prefix}-email-input`} />
    </div>
  </div>
);

/** Accept dialog: identity + optional comment + explicit authority confirmation. */
export const AcceptDialog = ({ open, onOpenChange, data, onDone }) => {
  const [f, setF] = useIdentity(data);
  const [message, setMessage] = useState("");
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { if (!open) { setConfirm(false); } }, [open]);
  const m = useMutation({
    mutationFn: () => post(`/public/quotes/${data.token}/accept`, { name: f.name.trim(), title: f.title?.trim() || null, email: f.email?.trim() || null, message: message.trim() || null, confirm: true }),
    onSuccess: (r) => { saveIdentity(f); toast.success(r.message || "Aksepten er registrert"); onOpenChange(false); onDone?.(); },
    onError: (e) => toast.error(errorMessage(e, "Kunne ikke registrere aksepten")),
  });
  const valid = f.name.trim().length >= 2 && confirm && (!f.email || /.+@.+\..+/.test(f.email));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl p-0 overflow-hidden" data-testid="proposal-accept-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="text-[20px] tracking-tight">Aksepter tilbud {data.quote.quote_number}</DialogTitle>
          <DialogDescription className="text-[13.5px] leading-6">Versjon {data.quote.version} · {nok(data.pricing.revenue)} eks. mva · gyldig til {fmtDate(data.quote.valid_until)}. Aksepten registreres med navn og tidspunkt – {data.seller?.name || "din kontaktperson"} tar kontakt for å avtale oppstart.</DialogDescription>
        </DialogHeader>
        <form className="space-y-5 px-6 pb-6 pt-4" onSubmit={(e) => { e.preventDefault(); if (valid) m.mutate(); }}>
          <IdentityFields f={f} setF={setF} prefix="proposal-accept" />
          <div className="space-y-1.5">
            <Label htmlFor="proposal-accept-message">Kommentar (valgfritt)</Label>
            <Textarea id="proposal-accept-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="F.eks. referanse/PO-nummer, ønsket kontaktform eller forbehold" data-testid="proposal-accept-message-input" />
          </div>
          <label className={cn("flex cursor-pointer items-start gap-3 rounded-xl border p-3.5 text-[13.5px] leading-6 transition-colors duration-150", confirm ? "border-primary/50 bg-primary/5" : "hover:bg-muted/50")}>
            <Checkbox checked={confirm} onCheckedChange={(v) => setConfirm(!!v)} className="mt-1" data-testid="proposal-accept-confirm-checkbox" />
            <span>Jeg bekrefter at jeg har fullmakt til å akseptere tilbudet på vegne av <span className="font-medium">{data.customer?.name}</span>, og at aksepten er bindende i henhold til vilkårene.</span>
          </label>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="ghost" className="h-10 px-4" onClick={() => onOpenChange(false)} data-testid="proposal-accept-cancel-button">Avbryt</Button>
            <Button type="submit" className="h-10 min-w-[180px] px-5" disabled={!valid || m.isPending} data-testid="proposal-accept-submit-button">
              {m.isPending ? <Loader2 className="animate-spin" /> : <CheckCircle2 />} Aksepter tilbudet
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/** Request-changes dialog: identity + message. */
export const ChangesDialog = ({ open, onOpenChange, data, onDone }) => {
  const [f, setF] = useIdentity(data);
  const [message, setMessage] = useState("");
  const m = useMutation({
    mutationFn: () => post(`/public/quotes/${data.token}/request-changes`, { name: f.name.trim(), title: f.title?.trim() || null, email: f.email?.trim() || null, message: message.trim() }),
    onSuccess: (r) => { saveIdentity(f); toast.success(r.message || "Forespørselen er sendt"); setMessage(""); onOpenChange(false); onDone?.(); },
    onError: (e) => toast.error(errorMessage(e, "Kunne ikke sende forespørselen")),
  });
  const valid = f.name.trim().length >= 2 && message.trim().length >= 3 && (!f.email || /.+@.+\..+/.test(f.email));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl p-0 overflow-hidden" data-testid="proposal-changes-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="text-[20px] tracking-tight">{data.state === "lapsed" ? "Be om oppdatert tilbud" : "Be om endringer"}</DialogTitle>
          <DialogDescription className="text-[13.5px] leading-6">Beskriv hva du ønsker justert – antall, moduler, datoer, pris eller vilkår. {data.seller?.name || "Din kontaktperson"} får meldingen umiddelbart og kommer tilbake med en oppdatert versjon.</DialogDescription>
        </DialogHeader>
        <form className="space-y-5 px-6 pb-6 pt-4" onSubmit={(e) => { e.preventDefault(); if (valid) m.mutate(); }}>
          <IdentityFields f={f} setF={setF} prefix="proposal-changes" />
          <div className="space-y-1.5">
            <Label htmlFor="proposal-changes-message">Hva ønsker du endret? <span className="text-destructive">*</span></Label>
            <Textarea id="proposal-changes-message" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="F.eks. «Vi trenger 2 ekstra kontormoduler og oppstart en uke tidligere.»" data-testid="proposal-changes-message-input" />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="ghost" className="h-10 px-4" onClick={() => onOpenChange(false)} data-testid="proposal-changes-cancel-button">Avbryt</Button>
            <Button type="submit" className="h-10 min-w-[170px] px-5" disabled={!valid || m.isPending} data-testid="proposal-changes-submit-button">
              {m.isPending ? <Loader2 className="animate-spin" /> : <MessageSquareText />} Send forespørsel
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const SellerCard = ({ data }) => {
  const s = data.seller || {};
  return (
    <div className="rounded-2xl bg-white/[0.06] p-6 ring-1 ring-white/10" data-testid="proposal-seller-card">
      <div className="text-[12.5px] text-white/50">Din kontaktperson</div>
      <div className="mt-4 flex items-center gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-[15px] font-semibold text-primary-foreground">{initialsOf(s.name || data.company?.name || "SI")}</div>
        <div className="min-w-0">
          <div className="truncate text-[16px] font-semibold" data-testid="proposal-seller-name">{s.name || data.company?.name}</div>
          <div className="truncate text-[13px] text-white/60">{s.title ? `${s.title} · ` : ""}{data.company?.name}</div>
        </div>
      </div>
      <div className="mt-5 space-y-2 text-[14px]">
        {s.phone && <a href={`tel:${String(s.phone).replace(/\s/g, "")}`} className="flex items-center gap-2.5 text-white/85 transition-colors duration-150 hover:text-white" data-testid="proposal-seller-phone"><Phone className="h-4 w-4 text-white/50" />{s.phone}</a>}
        {s.email && <a href={`mailto:${s.email}?subject=${encodeURIComponent(`Tilbud ${data.quote.quote_number}`)}`} className="flex items-center gap-2.5 text-white/85 transition-colors duration-150 hover:text-white" data-testid="proposal-seller-email"><Mail className="h-4 w-4 text-white/50" />{s.email}</a>}
      </div>
    </div>
  );
};

const copy = (data) => {
  const acc = data.response?.accepted;
  const seller = data.seller?.name || "din kontaktperson";
  switch (data.state) {
    case "accepted":
      return { title: "Takk – tilbudet er akseptert", text: acc?.name ? `Akseptert av ${acc.name}${acc.title ? `, ${acc.title}` : ""} ${fmtDateTime(acc.created_at)}. ${seller} tar kontakt for å avtale oppstart, leveringsplan og signering av leieavtale.` : `Tilbudet ble akseptert ${fmtDate(data.quote.accepted_at)}. ${seller} følger opp med leveringsplan.` };
    case "preview":
      return { title: "Forhåndsvisning", text: "Slik vil kunden se tilbudet når det sendes. Handlinger er deaktivert for utkast." };
    case "superseded":
      return { title: "Det finnes en nyere versjon", text: `Denne lenken viser versjon ${data.quote.version}. Be ${seller} om lenke til versjon ${data.quote.current_version}.` };
    case "lapsed":
      return { title: "Gyldigheten har utløpt", text: `Tilbudet var gyldig til ${fmtDate(data.quote.valid_until)}. Be om et oppdatert tilbud – vi bekrefter pris og kapasitet på nytt.` };
    case "declined":
      return { title: "Tilbudet er avsluttet", text: `Ta kontakt med ${seller} dersom dere ønsker å gjenoppta dialogen.` };
    default:
      return { title: "Klar til å gå videre?", text: `Aksepter tilbudet digitalt – det tar under ett minutt. Har du spørsmål eller ønsker justeringer, send oss en melding her, så kommer ${seller} tilbake med et oppdatert tilbud.` };
  }
};

/** 05 · Svar: the closing dark panel with the two actions, response state and the seller contact card. */
export const ProposalRespond = ({ data, onAccept, onChanges }) => {
  const c = copy(data);
  const changes = data.response?.change_requests || [];
  return (
    <section id="svar" className="proposal-section" aria-labelledby="svar-title" data-testid="proposal-section-svar">
      <div className="mx-auto max-w-[1180px] px-5 py-16 sm:px-8 lg:py-24">
        <div className="proposal-cover proposal-grain overflow-hidden rounded-[32px] text-white">
          <div className="relative grid gap-10 p-8 sm:p-12 lg:grid-cols-12 lg:p-16">
            <div className="lg:col-span-7">
              <div className="font-mono text-[12.5px] tabular text-white/50">05</div>
              <h2 id="svar-title" className="proposal-display mt-2 flex items-center gap-3 text-[32px] font-semibold leading-tight sm:text-[42px]" data-testid="proposal-respond-title">
                {data.state === "accepted" && <CheckDraw />}
                {c.title}
              </h2>
              <p className="mt-4 max-w-[540px] text-[16px] leading-7 text-white/70" data-testid="proposal-respond-text">{c.text}</p>

              {(data.can_accept || data.can_request_changes) && (
                <div className="mt-8 flex flex-wrap gap-3">
                  {data.can_accept && <Button onClick={onAccept} className="h-12 rounded-xl px-6 text-[15px] shadow-[0_12px_32px_-10px_hsl(var(--primary)/0.7)]" data-testid="proposal-accept-button"><CheckCircle2 /> Aksepter tilbudet</Button>}
                  {data.can_request_changes && <Button onClick={onChanges} variant="outline" className="h-12 rounded-xl border-white/25 bg-transparent px-6 text-[15px] text-white hover:bg-white/10 hover:text-white active:bg-white/15" data-testid="proposal-changes-button"><MessageSquareText /> {data.state === "lapsed" ? "Be om oppdatert tilbud" : "Be om endringer"}</Button>}
                </div>
              )}
              {data.state === "accepted" && (
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button asChild variant="outline" className="h-11 rounded-xl border-white/25 bg-transparent px-5 text-[14px] text-white hover:bg-white/10 hover:text-white"><a href={publicPdfUrl(data.token)} target="_blank" rel="noopener noreferrer" data-testid="proposal-accepted-pdf-link"><FileDown /> Last ned tilbudet (PDF)</a></Button>
                </div>
              )}
              {data.can_accept && <p className="mt-4 text-[12.5px] text-white/45">Ved aksept registreres navn, tidspunkt og eventuell kommentar. Leieavtale signeres i etterkant.</p>}

              {changes.length > 0 && (
                <div className="mt-10" data-testid="proposal-change-requests">
                  <div className="text-[12.5px] text-white/50">Dine forespørsler</div>
                  <ul className="mt-3 space-y-3">
                    {changes.map((r) => (
                      <li key={r.id} className="rounded-xl bg-white/[0.06] p-4 ring-1 ring-white/10" data-testid={`proposal-change-request-${r.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2 text-[12.5px] text-white/55">
                          <span>{r.name}{r.title ? `, ${r.title}` : ""}</span>
                          <span className="inline-flex items-center gap-1.5 font-mono tabular"><Clock className="h-3.5 w-3.5" />{fmtDateTime(r.created_at)}</span>
                        </div>
                        <p className="mt-2 text-[14px] leading-6 text-white/85">{r.message}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="lg:col-span-5 lg:self-end"><SellerCard data={data} /></div>
          </div>
        </div>
      </div>
    </section>
  );
};

/** Fixed bottom action bar for small screens (the header hides the primary action there). */
export const MobileActionBar = ({ data, onAccept, onChanges }) => {
  if (!data.can_accept && !data.can_request_changes) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border/70 bg-background/90 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-xl sm:hidden" data-testid="proposal-mobile-bar">
      <div className="flex gap-2">
        {data.can_request_changes && <Button variant="outline" onClick={onChanges} className="h-11 flex-1 rounded-xl text-[14px]" data-testid="proposal-mobile-changes-button">{data.state === "lapsed" ? "Oppdatert tilbud" : "Be om endringer"}</Button>}
        {data.can_accept && <Button onClick={onAccept} className="h-11 flex-1 rounded-xl text-[14px]" data-testid="proposal-mobile-accept-button">Aksepter tilbud</Button>}
      </div>
    </div>
  );
};
